import { useCallback, useEffect, useRef, useState } from 'react';

import { Iconos } from '../componentes/iconos';
import { CamaraDeReja } from '../componentes/CamaraDeReja';
import { VideoGuardia } from '../componentes/VideoGuardia';
import { Boton, Chip, Encabezado, Entrada, ErrorCaja, Panel, Seleccion } from '../componentes/ui';
import { almacenToken, ErrorApi, pedir } from '../lib/api';
import { usarPantallaEncendida } from '../lib/pantalla';
import { useSesion } from '../lib/sesion';
import { useFlujoDeCaseta } from '../lib/flujo';
import { baseApi } from '../lib/servidor';

/**
 * Caseta del guardia — validación de las visitas del kiosco, control de carriles
 * y avisos de lo que esté fallando.
 *
 * Sigue el sketch «Caseta de Guardia» del sistema de diseño: las fotos que tomó
 * el kiosco con sus dos botones grandes, el control de carriles a la derecha y
 * el control de carriles a la derecha. Lo que era una barra con todos los
 * aparatos del condominio es ahora un aviso que solo aparece cuando algo falla
 * **en esta reja**: ver caerse la entrada de al lado no le sirve a quien no
 * puede atenderla, y un panel siempre encendido deja de leerse.
 *
 * Todo lo que se ve obedece al acceso elegido en el selector —visitas, carriles
 * y avisos—, y para el guardia ese acceso no se elige: es el que tiene
 * asignado, y el servidor no le sirve otro.
 *
 * Por qué existe: el visitante con QR ya viene autorizado por el residente, pero
 * la pluma no abre hasta que un guardia mira la foto de la identificación y da
 * el visto bueno (docs/09, camino 1). Esta pantalla es ese paso.
 *
 * No hace falta recargar: el servidor avisa por un flujo abierto (SSE) en
 * cuanto algo cambia, y un sondeo lento queda de respaldo por si ese flujo se
 * muere sin decirlo. El guardia no puede estar dándole a recargar con un coche
 * detenido en la reja.
 */

interface FotoPendiente {
  id: string;
  tipo: 'ROSTRO' | 'IDENTIFICACION' | 'VEHICULO';
  ruta: string;
}

interface VisitaPendiente {
  id: string;
  /** `EN_CAPTURA` mientras el visitante acomoda la credencial en la charola. */
  estado: 'EN_CAPTURA' | 'PENDIENTE_AUTORIZACION';
  nombreVisitante: string | null;
  /** Qué tan seguro está el OCR. Nulo cuando el nombre lo escribió una persona. */
  confianzaNombre: 'ALTA' | 'MEDIA' | null;
  entradaEn: string;
  /** La última orden que el guardia le mandó al kiosco, si sigue vigente. */
  instruccion: string | null;
  credencialAceptadaEn: string | null;
  /** No nulo mientras el OCR corre sobre la foto recién capturada. */
  credencialLeyendoEn: string | null;
  /** De dónde viene: con invitación del residente, o sin ella. */
  origen: string;
  /** No nulo desde que el guardia le entregó un plástico. */
  tarjetonId: string | null;
  /** La regla del fraccionamiento: sin código, no se pasa sin tarjetón. */
  condominio: { exigirTarjetonSinQr: boolean };
  puntoAcceso: { id: string; nombre: string } | null;
  /** En qué reja está parado. Nulo si el kiosco atiende el acceso entero. */
  carril: { id: string; nombre: string } | null;
  unidad: {
    clave: string;
    /** Solo etiquetas y su lugar en la cascada: el número nunca sale del servidor. */
    telefonos: { orden: number; etiqueta: string | null }[];
  } | null;
  fotos: FotoPendiente[];
}

interface Carril {
  id: string;
  nombre: string;
  sentido: 'ENTRADA' | 'SALIDA' | 'BIDIRECCIONAL';
  puntoAcceso: string;
  tienePluma: boolean;
  /** La cámara que mira esta reja, si la hay. */
  camaraId: string | null;
  /**
   * Cómo terminó la última orden de este carril. Es la única forma que tiene el
   * guardia de enterarse de una apertura que salió del kiosco: esa no la pidió
   * él, así que no hay respuesta donde contárselo.
   */
  ultimaApertura: {
    cuando: string;
    acusada: boolean;
    exitosa: boolean;
    error: string | null;
  } | null;
}

/** Lo que responde `POST /caseta/carriles/:id/abrir`, ya con el acuse. */
interface ResultadoApertura {
  id: string;
  solicitudId: string;
  acusado: boolean;
  exitosa: boolean;
  error: string | null;
}

/** Un aparato que **no** responde. Los que están bien no viajan: no hay nada que decir de ellos. */
interface Alerta {
  id: string;
  nombre: string;
  tipo: string;
  ultimoLatido: string | null;
  puntoAcceso: { nombre: string } | null;
}

/**
 * Cada cuánto se pregunta por visitas nuevas **cuando no hay flujo**.
 *
 * Corto a propósito: del otro lado hay un visitante parado esperando a que el
 * guardia lo vea, y ese es el único dato de esta pantalla que corre prisa.
 */
const REFRESCO_VISITAS_MS = 1500;

/**
 * Y cada cuánto **con el flujo vivo**.
 *
 * El sondeo no desaparece al tener SSE, solo se afloja. Una conexión larga se
 * puede morir de formas que no dan error —un intermediario que la olvida, una
 * tablet que se suspende— y el vigilante de latidos cubre casi todas, pero no
 * hay razón para apostar la pantalla del guardia a que las cubre todas. Una
 * consulta cada quince segundos es el precio de no depender de eso.
 */
const REFRESCO_VISITAS_CON_FLUJO_MS = 15_000;

/**
 * El estado de carriles y avisos. Va lento porque cambia lento: un aparato
 * se cae o se levanta, y el latido de MQTT ya tiene su propia holgura.
 */
const REFRESCO_ESTADO_MS = 10_000;

/**
 * El acuse en pantalla dura más que los 3 s que el controlador tiene para
 * responder, para que el guardia no vuelva a picarle mientras la pluma sube.
 */
const AVISO_APERTURA_MS = 4000;

const SENTIDOS: Record<Carril['sentido'], string> = {
  ENTRADA: 'Entrada',
  SALIDA: 'Salida',
  BIDIRECCIONAL: 'Ambos sentidos',
};

const TIPOS_APARATO: Record<string, string> = {
  KIOSCO: 'Kiosco',
  LECTOR_QR: 'Lector QR',
  CAMARA_CREDENCIAL: 'Cámara de credencial',
  LECTOR_UHF: 'Lector UHF',
  CONTROLADOR_PLUMA: 'Pluma',
  DETECTOR_PASO: 'Detector de paso',
  TELEFONO_IP: 'Teléfono',
  LECTOR_FACIAL: 'Lector facial',
  CAMARA_LPR: 'Cámara de placas',
  CAMARA_ROSTRO: 'Cámara de rostro',
};

/**
 * En qué caseta está esta tablet. Se recuerda **en el aparato** y no en la
 * cuenta: el guardia rota de puesto y la tablet no se mueve, así que la
 * pregunta «¿qué reja atiendo?» la contesta mejor el mueble que la persona.
 */
const CLAVE_PUNTO = 'kodica.puntoAcceso';

/**
 * Por qué no pasa. Cuatro, y no un campo de texto libre: el guardia tiene a
 * alguien esperando y no va a escribir una frase. Cuatro botones se responden
 * en un segundo y dejan algo consultable después, que es de lo que se trata.
 */
const MOTIVOS_RECHAZO = [
  'No corresponde la persona',
  'La identificación no sirve',
  'El residente no lo autoriza',
  'Otro motivo',
];

/**
 * Las visitas repartidas por carril, en el orden en que llegaron.
 *
 * Un fraccionamiento puede tener más de un carril de visitas, cada uno con su
 * FastPass. Con una sola lista, el guardia no sabe en qué reja está parado quien
 * espera: ve tres tarjetas y tiene que adivinar cuál es la del coche que tiene
 * enfrente. Agrupadas, cada sección es una reja.
 *
 * Las que no traen carril van juntas al final: son las del kiosco que atiende el
 * acceso completo, o accesos anteriores a que esto se registrara.
 */
function agruparPorCarril(visitas: VisitaPendiente[]): [string, VisitaPendiente[]][] {
  const SIN = 'Sin carril';
  const grupos = new Map<string, VisitaPendiente[]>();
  for (const v of visitas) {
    const clave = v.carril?.nombre ?? SIN;
    const lista = grupos.get(clave);
    if (lista) lista.push(v);
    else grupos.set(clave, [v]);
  }
  return [...grupos.entries()].sort(([a], [b]) =>
    a === SIN ? 1 : b === SIN ? -1 : a.localeCompare(b, 'es'),
  );
}

export function CasetaPagina() {
  const { usuario } = useSesion();
  // La tablet de la caseta no puede apagarse sola: despertarla y desbloquearla
  // son los segundos que no hay con un coche esperando en la reja.
  usarPantallaEncendida(true);
  // Administración y jefatura miran cualquier reja; el guardia, la suya.
  const puedeNavegar = usuario ? usuario.rol !== 'GUARDIA' : false;
  const [visitas, setVisitas] = useState<VisitaPendiente[] | null>(null);
  const [puntos, setPuntos] = useState<{ id: string; nombre: string }[]>([]);
  const [punto, setPunto] = useState<string>(() => localStorage.getItem(CLAVE_PUNTO) ?? '');
  const [carriles, setCarriles] = useState<Carril[] | null>(null);
  const [alertas, setAlertas] = useState<Alerta[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [abriendo, setAbriendo] = useState<string | null>(null);
  const [ordenado, setOrdenado] = useState<ResultadoApertura | null>(null);
  const avisoTemporizador = useRef<number | null>(null);

  /** Lo urgente: quién está esperando en el kiosco ahora mismo. */
  const cargarVisitas = useCallback(async () => {
    try {
      const ruta = punto
        ? `/accesos/pendientes?puntoAccesoId=${encodeURIComponent(punto)}`
        : '/accesos/pendientes';
      setVisitas(await pedir<VisitaPendiente[]>(ruta));
      setError(null);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo consultar la caseta');
    }
  }, [punto]);

  /**
   * Lo que cambia despacio. Va aparte de las visitas para poder consultarlo con
   * mucha menos frecuencia; antes las tres cosas viajaban juntas y la lista de
   * aparatos marcaba el ritmo de todo.
   *
   * Los fallos aquí se tragan a propósito: que el broker no conteste por los
   * aparatos no puede tapar la pantalla con un error cuando lo que el guardia
   * necesita —las visitas— llegó bien.
   */
  // Los puntos de acceso solo se leen una vez: cambian cuando alguien instala
  // una caseta nueva, no durante un turno.
  useEffect(() => {
    pedir<{ id: string; nombre: string }[]>('/topologia/puntos-acceso')
      .then(setPuntos)
      .catch(() => undefined);
  }, []);

  // **Depende del acceso elegido.** Las dos consultas lo llevan, y `punto` va en
  // las dependencias: con la lista vacía que tenía antes, el cierre se quedaba
  // con el primer valor y cambiar el selector no movía nada de la pantalla.
  const cargarEstado = useCallback(async () => {
    const sufijo = punto ? `?puntoAccesoId=${encodeURIComponent(punto)}` : '';
    const [listaCarriles, listaAlertas] = await Promise.allSettled([
      pedir<Carril[]>(`/caseta/carriles${sufijo}`),
      pedir<Alerta[]>(`/caseta/alertas${sufijo}`),
    ]);
    if (listaCarriles.status === 'fulfilled') setCarriles(listaCarriles.value);
    if (listaAlertas.status === 'fulfilled') setAlertas(listaAlertas.value);
  }, [punto]);

  const cargar = useCallback(async () => {
    await Promise.all([cargarVisitas(), cargarEstado()]);
  }, [cargarVisitas, cargarEstado]);

  // El servidor avisa en cuanto algo cambia; esto es lo que hace que una visita
  // nueva aparezca en decenas de milisegundos en vez de en el próximo sondeo.
  const { conectado: flujoVivo } = useFlujoDeCaseta(cargarVisitas);

  useEffect(() => {
    void cargar();
    const ritmo = flujoVivo ? REFRESCO_VISITAS_CON_FLUJO_MS : REFRESCO_VISITAS_MS;
    const tVisitas = setInterval(() => void cargarVisitas(), ritmo);
    const tEstado = setInterval(() => void cargarEstado(), REFRESCO_ESTADO_MS);

    // Los navegadores frenan los temporizadores de una pestaña que no se ve:
    // Chrome los deja en uno por minuto. En una tablet de caseta eso pasa cada
    // vez que se apaga la pantalla o el guardia abre otra cosa, y al volver la
    // lista puede llevar un minuto sin mirarse — con un visitante esperando
    // enfrente y sin nada en pantalla que lo delate.
    //
    // Consultar al recuperar la visibilidad también despega el mensaje de
    // error si la caseta estuvo caída: sin esto se queda hasta el siguiente
    // tic aunque el servidor ya haya vuelto.
    function alVolverAVerse() {
      if (document.visibilityState === 'visible') void cargar();
    }
    document.addEventListener('visibilitychange', alVolverAVerse);

    return () => {
      clearInterval(tVisitas);
      clearInterval(tEstado);
      document.removeEventListener('visibilitychange', alVolverAVerse);
    };
  }, [cargar, cargarVisitas, cargarEstado, flujoVivo]);

  useEffect(() => {
    return () => {
      if (avisoTemporizador.current) window.clearTimeout(avisoTemporizador.current);
    };
  }, []);

  async function resolver(id: string, accion: 'liberar' | 'rechazar', motivo?: string) {
    setOcupado(id);
    try {
      // El motivo va al servidor desde siempre —el endpoint lo acepta— pero la
      // caseta nunca se lo mandaba, así que la bitácora quedaba con rechazos
      // sin explicación. Sirve justo cuando alguien reclama meses después.
      await pedir(`/accesos/${id}/${accion}`, {
        metodo: 'POST',
        ...(motivo ? { cuerpo: { motivo } } : {}),
      });
      // Se quita de la lista al momento: con el refresco de 5 s la tarjeta
      // seguiría ahí un rato y el guardia volvería a picarle.
      setVisitas((v) => (v ?? []).filter((x) => x.id !== id));
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo registrar la decisión');
      void cargar();
    } finally {
      setOcupado(null);
    }
  }

  async function pedirOtraFoto(id: string) {
    setOcupado(id);
    try {
      await pedir(`/accesos/${id}/recapturar`, { metodo: 'POST' });
      setError(null);
      // La visita **no** se quita de la lista: sigue pendiente del visto bueno,
      // y en unos segundos volverá con la foto nueva. Quitarla haría pensar que
      // se resolvió.
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo pedir otra foto');
    } finally {
      setOcupado(null);
    }
  }

  /**
   * El guardia dirige la captura: capturar, voltear, credencial no válida o
   * repetir.
   *
   * La visita **no** se quita de la lista ni cambia de fase aquí. La orden se
   * manda y se espera a que el kiosco la cumpla: hasta que llegue la foto, el
   * guardia sigue viendo el video. Adelantarse a pintar la fase siguiente le
   * haría creer que ya se capturó algo que todavía no existe.
   */
  async function instruir(id: string, instruccion: string) {
    setOcupado(id);
    try {
      await pedir(`/accesos/${id}/instruir`, { metodo: 'POST', cuerpo: { instruccion } });
      setError(null);
      // Se recarga en el momento para que el aviso de «le pediste que la
      // voltee» aparezca sin esperar al sondeo.
      await cargarVisitas();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo mandar la instrucción');
    } finally {
      setOcupado(null);
    }
  }

  /** El guardia da por buena la credencial capturada y su nombre. */
  async function aceptarCredencial(id: string, nombre: string) {
    setOcupado(id);
    try {
      await pedir(`/accesos/${id}/credencial`, {
        metodo: 'POST',
        cuerpo: { nombreVisitante: nombre },
      });
      setError(null);
      await cargarVisitas();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo aceptar la credencial');
    } finally {
      setOcupado(null);
    }
  }

  async function abrirCarril(id: string) {
    setAbriendo(id);
    try {
      // La petición tarda hasta 3 s: el servidor espera el acuse del controlador
      // antes de contestar. Es a propósito. La pluma está fuera de la vista del
      // guardia en varios condominios, y decirle "orden enviada" cuando el
      // aparato no contestó lo deja esperando frente a una reja que no se mueve.
      const r = await pedir<ResultadoApertura>(`/caseta/carriles/${id}/abrir`, { metodo: 'POST' });
      setError(null);
      setOrdenado(r);
      if (avisoTemporizador.current) window.clearTimeout(avisoTemporizador.current);
      avisoTemporizador.current = window.setTimeout(() => setOrdenado(null), AVISO_APERTURA_MS);
      // Refresca la lista para que la última apertura del carril quede al día.
      void cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo abrir el carril');
    } finally {
      setAbriendo(null);
    }
  }

  const pendientes = visitas ?? [];

  return (
    <>
      <Encabezado titulo="Caseta" descripcion="Visitas del kiosco esperando tu visto bueno">
        {pendientes.length > 0 && <Chip tono="aviso">{pendientes.length} en espera</Chip>}
        {/* **Solo para quien supervisa.** El guardia no elige qué reja atiende:
            se lo dice su asignación, y el servidor solo le sirve la suya —así
            que un selector aquí sería una lista de opciones que no cambian
            nada—. Quien administra o dirige la seguridad sí navega entre todas,
            y su elección la recuerda el aparato: la tablet no se mueve de la
            caseta aunque la persona rote.

            Con un solo acceso tampoco hay nada que elegir. */}
        {puedeNavegar && puntos.length > 1 && (
          <div className="w-56">
            <Seleccion
              aria-label="Qué acceso se está mirando"
              value={punto}
              onChange={(e) => {
                setPunto(e.target.value);
                localStorage.setItem(CLAVE_PUNTO, e.target.value);
              }}
            >
              <option value="">Todos los accesos</option>
              {puntos.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre}
                </option>
              ))}
            </Seleccion>
          </div>
        )}
      </Encabezado>

      {error && (
        <div className="mb-stack-md">
          <ErrorCaja>{error}</ErrorCaja>
        </div>
      )}

      {/* Un guardia sin reja asignada no ve **nada**, y eso es correcto: es una
          cuenta a medio configurar y enseñarle todo «mientras tanto»
          convertiría un olvido administrativo en un permiso. Lo que no puede
          pasar es que lo descubra a las tres de la mañana mirando una pantalla
          vacía y llamando por teléfono. Se le dice qué falta y a quién pedirlo. */}
      {!puedeNavegar && carriles !== null && carriles.length === 0 && (
        <div className="mb-stack-md">
          <ErrorCaja>
            Tu cuenta todavía no tiene una reja asignada, así que no verás visitas ni podrás abrir
            ninguna pluma. Pídele a administración o al jefe de seguridad que te asigne tu acceso.
          </ErrorCaja>
        </div>
      )}

      {/* **El control de plumas al lado desde una tablet, no desde un
          escritorio.** Estaba en `xl` (1280 px) y una tablet de 10 pulgadas en
          horizontal ronda los 1024: el guardia se quedaba con los botones de
          pluma **debajo** de las visitas, y tenía que desplazarse con un coche
          parado en la reja. Abrir una pluma es lo más urgente que hace; no
          puede estar fuera de la pantalla. */}
      <div className="grid gap-gutter lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div>
          {visitas === null && !error && (
            <Panel>
              <p className="p-6 text-body-md text-on-surface-variant">Consultando la caseta…</p>
            </Panel>
          )}

          {visitas !== null && pendientes.length === 0 && (
            <Panel>
              <p className="p-6 text-body-md text-on-surface-variant">
                No hay visitas esperando. Las nuevas aparecen solas.
              </p>
            </Panel>
          )}

          {agruparPorCarril(pendientes).map(([carril, delCarril]) => (
            <section key={carril} className="mb-stack-md">
              {/* El encabezado solo cuando hay más de un carril con gente. Con
                  uno, un título encima de la única lista es ruido. */}
              {agruparPorCarril(pendientes).length > 1 && (
                <h2 className="mb-stack-sm flex items-center gap-2 text-label-bold uppercase tracking-wider text-on-surface-variant">
                  {carril}
                  <Chip tono="aviso">{delCarril.length}</Chip>
                </h2>
              )}
              {/* Dos columnas de visitas solo desde 1280. En una tablet, con
                  el panel de plumas ya ocupando su parte, dos tarjetas al lado
                  dejan la charola y la cara tan chicas que no se distingue una
                  cara — y para eso están. */}
              <div className="grid gap-gutter xl:grid-cols-2">
                {delCarril.map((v) => (
                  <TarjetaValidacion
                    key={v.id}
                    visita={v}
                    // La cámara sale del carril donde está la visita: la
                    // tarjeta no tiene por qué saber de topología.
                    camaraId={(carriles ?? []).find((c) => c.id === v.carril?.id)?.camaraId ?? null}
                    ocupado={ocupado === v.id}
                    onResolver={resolver}
                    onOtraFoto={pedirOtraFoto}
                    onInstruir={(id, i) => void instruir(id, i)}
                    onAceptarCredencial={(id, n) => void aceptarCredencial(id, n)}
                    onTarjeton={() => void cargar()}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>

        <div className="flex flex-col gap-gutter">
          <DevolverTarjeton alCerrarVisita={() => void cargar()} />
          <ControlCarriles
            carriles={carriles}
            abriendo={abriendo}
            ordenado={ordenado}
            onAbrir={abrirCarril}
          />
        </div>
      </div>

      <Alertas alertas={alertas} />
    </>
  );
}

/**
 * Control de carriles: la salida manual cuando el tag no lee, el QR está
 * borroso o entra una ambulancia. Cada apertura queda auditada con el nombre
 * del guardia, así que el botón no es un atajo sino un camino registrado.
 */
function ControlCarriles({
  carriles,
  abriendo,
  ordenado,
  onAbrir,
}: {
  carriles: Carril[] | null;
  abriendo: string | null;
  ordenado: ResultadoApertura | null;
  onAbrir: (id: string) => void;
}) {
  return (
    <Panel className="h-fit">
      <div className="border-b border-outline-variant px-6 py-4">
        <h2 className="text-headline-md text-on-surface">Control de carriles</h2>
        <p className="mt-1 text-label-sm text-on-surface-variant">
          Toda apertura queda a tu nombre en la bitácora
        </p>
      </div>

      {carriles === null && (
        <p className="p-6 text-body-md text-on-surface-variant">Consultando los carriles…</p>
      )}

      {carriles !== null && carriles.length === 0 && (
        <p className="p-6 text-body-md text-on-surface-variant">
          Este acceso no tiene carriles configurados.
        </p>
      )}

      <ul className="divide-y divide-outline-variant">
        {(carriles ?? []).map((c) => (
          <li key={c.id} className="px-6 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-body-md text-on-surface">{c.nombre}</p>
                <p className="truncate text-label-sm text-on-surface-variant">
                  {c.puntoAcceso} · {SENTIDOS[c.sentido]}
                </p>
                {/* El aviso de la última apertura fallida se queda puesto, sin
                  temporizador: viene del camino del kiosco, donde el guardia no
                  estaba mirando cuando ocurrió. Desaparece solo cuando una
                  apertura posterior sí funcione. */}
                {c.ultimaApertura && !c.ultimaApertura.exitosa && (
                  <p className="mt-1 text-label-sm text-error">
                    {c.ultimaApertura.acusada
                      ? `La pluma reportó: ${c.ultimaApertura.error ?? 'no pudo abrir'}`
                      : 'La última orden no tuvo respuesta. Puede que haya que abrir a mano.'}
                  </p>
                )}
              </div>

              {ordenado?.id === c.id ? (
                ordenado.exitosa ? (
                  <Chip tono="ok">Abrió</Chip>
                ) : (
                  <Chip tono="malo">{ordenado.acusado ? 'No pudo abrir' : 'Sin respuesta'}</Chip>
                )
              ) : (
                <Boton
                  variante="tonal"
                  icono={Iconos.entrar}
                  // Sin controlador no hay a quién mandarle la orden: el botón se
                  // apaga en vez de fallar después de que el guardia lo pica.
                  disabled={!c.tienePluma || abriendo === c.id}
                  onClick={() => onAbrir(c.id)}
                >
                  {abriendo === c.id ? 'Abriendo…' : 'Abrir'}
                </Boton>
              )}
            </div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * Avisos del sistema: **lo que está fallando en esta reja, y nada más**.
 *
 * Antes esto era una barra con todos los aparatos del condominio, verdes y
 * rojos en fila. Se quitó por dos razones. La primera es que el guardia de una
 * reja veía caerse la de al lado, que ni atiende ni puede arreglar. La segunda
 * es más de fondo: un panel que enseña doce cosas bien y una mal obliga a
 * buscar la mala, y un aviso que está siempre puesto deja de leerse a la
 * semana.
 *
 * Cuando todo responde, esto **no dibuja nada**. El silencio es la buena
 * noticia, y así lo que aparece aquí siempre significa algo.
 */
function Alertas({ alertas }: { alertas: Alerta[] | null }) {
  // Mientras se consulta tampoco se dibuja: un «consultando…» permanente en el
  // sitio donde viven las alarmas entrena a mirar hacia otro lado.
  if (!alertas || alertas.length === 0) return null;

  return (
    <Panel className="mt-gutter border-2 border-error">
      <div className="flex flex-col gap-2 px-6 py-4">
        <span className="text-label-bold uppercase tracking-wider text-error">
          {alertas.length === 1
            ? 'Un aparato no responde'
            : `${alertas.length} aparatos no responden`}
        </span>
        <ul className="flex flex-col gap-1">
          {alertas.map((a) => (
            <li key={a.id} className="text-body-md text-on-surface">
              <Chip tono="malo">{TIPOS_APARATO[a.tipo] ?? a.tipo}</Chip>{' '}
              <span className="font-medium">{a.nombre}</span>{' '}
              <span className="text-on-surface-variant">— {desdeCuando(a)}</span>
            </li>
          ))}
        </ul>
        <span className="text-label-sm text-on-surface-variant">
          Avisa a la administración. Mientras tanto, las plumas se pueden abrir a mano desde aquí.
        </span>
      </div>
    </Panel>
  );
}

/**
 * Desde cuándo no responde.
 *
 * «Nunca se ha conectado» y «dejó de responder a las 3:40» son averías
 * distintas y se arreglan distinto: la primera es una instalación sin terminar
 * —cable, corriente, configuración— y la segunda es algo que funcionaba y se
 * cayó. Decir «sin conexión» para las dos deja al guardia sin saber qué contar
 * cuando llame.
 */
function desdeCuando(a: Alerta): string {
  if (!a.ultimoLatido) return 'nunca se ha conectado';

  return `sin señal desde ${new Date(a.ultimoLatido).toLocaleString('es-MX', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })}`;
}

/**
 * Las tres fases por las que pasa una visita en la pantalla del guardia.
 *
 * No son estados de la base: se derivan de ella. `EN_CAPTURA` sin foto de
 * identificación significa que el visitante está acomodando la credencial ahora
 * mismo; con foto, que ya se tomó y falta darla por buena.
 */
type Fase = 'captura' | 'credencial' | 'autorizacion';

function faseDe(visita: VisitaPendiente): Fase {
  if (visita.estado !== 'EN_CAPTURA') return 'autorizacion';
  // Cualquier instrucción de captura en curso —repetir, voltear, «no es
  // identificación»— manda: el guardia está dirigiendo una toma nueva, así que
  // se ve el video, no una foto. El servidor ya borra la anterior al pedir
  // repetir; esto es el cinturón por si una foto residual llega en el mismo
  // sondeo que la instrucción.
  if (
    visita.instruccion === 'REPETIR' ||
    visita.instruccion === 'VOLTEAR' ||
    visita.instruccion === 'NO_ES_CREDENCIAL'
  ) {
    return 'captura';
  }
  return visita.fotos.some((f) => f.tipo === 'IDENTIFICACION') ? 'credencial' : 'captura';
}

const TEXTO_INSTRUCCION: Record<string, string> = {
  CAPTURAR: 'Capturando…',
  VOLTEAR: 'Le pediste que la voltee',
  NO_ES_CREDENCIAL: 'Le pediste una identificación válida',
  REPETIR: 'Le pediste que la coloque otra vez',
};

function TarjetaValidacion({
  visita,
  ocupado,
  onResolver,
  onOtraFoto,
  onInstruir,
  onAceptarCredencial,
  onTarjeton,
  camaraId,
}: {
  visita: VisitaPendiente;
  /** La cámara del carril donde está parada esta visita, si la hay. */
  camaraId: string | null;
  ocupado: boolean;
  onResolver: (id: string, accion: 'liberar' | 'rechazar', motivo?: string) => void;
  onOtraFoto: (id: string) => void;
  onInstruir: (id: string, instruccion: string) => void;
  onAceptarCredencial: (id: string, nombre: string) => void;
  /**
   * Recargar la lista en cuanto se entrega un tarjetón.
   *
   * Sin esto había que esperar al sondeo, que con el video en vivo baja a
   * 15 s: el guardia entregaba el plástico y el botón seguía diciendo
   * «Falta el tarjetón» un cuarto de minuto. Eso no se lee como una espera,
   * se lee como que el sistema se trabó.
   */
  onTarjeton: () => void;
}) {
  // Al rechazar se piden los motivos, y **los propios motivos son la
  // confirmación**: un diálogo de «¿seguro?» que no aporta nada se aprende a
  // despachar sin leerlo, y encima no deja rastro de por qué se rechazó.
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);
  const identificacion = visita.fotos.find((f) => f.tipo === 'IDENTIFICACION');
  const rostro = visita.fotos.find((f) => f.tipo === 'ROSTRO');
  const fase = faseDe(visita);
  const leyendo = visita.credencialLeyendoEn !== null;

  const sugerido = visita.nombreVisitante ?? '';
  const [nombre, setNombre] = useState(sugerido);

  // El nombre del campo se ata a la identidad de la **foto**, no a la visita.
  //
  //  · Foto nueva (una recaptura): se adopta sin más lo que el servidor tenga
  //    para ella. El OCR de la foto nueva reemplaza lo que hubiera —incluida una
  //    lectura vieja o una corrección de la foto anterior—, que es lo que se
  //    espera de repetir: releer de cero.
  //  · Misma foto: solo se rellena si el guardia no ha escrito nada, para no
  //    pisarle su corrección y para adoptar el OCR que llega un par de segundos
  //    tarde.
  //
  // Sin esta distinción, el campo conservaba el nombre de la primera foto aunque
  // el guardia pidiera otra: el servidor ya reescribía, pero el navegador se
  // quedaba con lo viejo.
  const fotoIdRef = useRef<string | undefined>(identificacion?.id);
  useEffect(() => {
    if (identificacion?.id !== fotoIdRef.current) {
      fotoIdRef.current = identificacion?.id;
      setNombre(sugerido);
    } else {
      setNombre((actual) => (actual.trim() === '' ? sugerido : actual));
    }
  }, [identificacion?.id, sugerido]);

  async function guardarNombre() {
    const limpio = nombre.trim();
    if (limpio.length < 2 || limpio === sugerido) return;
    try {
      await pedir(`/accesos/${visita.id}/nombre`, {
        metodo: 'PATCH',
        cuerpo: { nombreVisitante: limpio },
      });
    } catch {
      // No se interrumpe al guardia por esto: tiene un coche esperando y el
      // nombre se puede corregir después desde la bitácora.
    }
  }

  // **Con invitación o sin ella** es la diferencia más importante de la tarjeta
  // y hasta ahora no se veía. Con invitación, el residente ya dijo que sí y el
  // guardia solo comprueba que la persona corresponde. Sin ella, nadie la
  // espera: hay que llamar a la vivienda antes de dejar pasar. Que las dos se
  // vieran iguales invitaba a tratarlas igual.
  const invitada = visita.origen === 'KIOSCO_QR';

  // La misma regla que aplica el servidor al liberar, dicha antes de pulsar.
  // No sustituye a la del servidor —esta pantalla se puede saltar— pero
  // convierte un error contra el que se choca en una condición que se ve.
  const faltaTarjeton =
    !invitada && visita.condominio.exigirTarjetonSinQr && visita.tarjetonId === null;

  const destino = (
    <div
      className={`rounded-xl px-5 py-4 text-center ${
        invitada ? 'bg-surface-container-high' : 'border-2 border-tertiary bg-tertiary-container/20'
      }`}
    >
      <p className="text-label-bold uppercase tracking-wider text-on-surface-variant">
        Destino solicitado
      </p>
      <p className="mt-1 text-headline-lg text-primary">{visita.unidad?.clave ?? '—'}</p>
      <p className="mt-2 text-body-md text-on-surface">
        {invitada ? 'Invitado por el residente' : 'Sin invitación — confirma antes de dejar pasar'}
      </p>
      <LlamarVivienda visita={visita} />
      {/* El tarjetón es lo que le da a esta visita con qué cerrar su ciclo al
          salir. Solo aparece sin invitación: la que trae QR ya sale con él. */}
      {!invitada && <EntregarTarjeton visita={visita} alEntregar={onTarjeton} />}
      {faltaTarjeton && (
        <p className="mt-3 text-body-md text-tertiary">Esta visita no puede pasar sin tarjetón.</p>
      )}
    </div>
  );

  // El OCR marca sus lecturas dudosas, y aquí eso **cuesta un gesto**: con una
  // lectura MEDIA el guardia tiene que confirmar el nombre antes de aceptar la
  // credencial. Si aprobar una lectura dudosa costara lo mismo que aprobar una
  // buena, la distinción sería un adorno y el guardia acabaría sellando las dos
  // igual — que es exactamente el error que se quiere evitar.
  const dudosa = visita.confianzaNombre === 'MEDIA' && nombre.trim() === sugerido;

  const campoNombre = (
    <div>
      <label
        className="text-label-bold uppercase tracking-wider text-on-surface-variant"
        htmlFor={`nombre-${visita.id}`}
      >
        Visitante
      </label>
      <Entrada
        id={`nombre-${visita.id}`}
        value={nombre}
        onChange={(e) => setNombre(e.target.value)}
        onBlur={() => void guardarNombre()}
        placeholder="Nombre de la credencial"
        // Con una lectura dudosa el texto arranca seleccionado: corregirlo es
        // teclear encima, no borrar primero.
        autoFocus={dudosa}
        onFocus={dudosa ? (e) => e.currentTarget.select() : undefined}
        className={dudosa ? 'border-tertiary' : undefined}
      />
      {dudosa && (
        <p className="mt-1 flex items-start gap-2 text-body-md text-on-surface">
          <Iconos.alerta tam={18} className="mt-0.5 shrink-0 text-tertiary" />
          <span>La lectura no fue clara. Compárala con la credencial antes de aceptarla.</span>
        </p>
      )}
      {sugerido && nombre.trim() !== sugerido && (
        <p className="mt-1 text-body-md text-on-surface-variant">El kiosco leyó: {sugerido}</p>
      )}
    </div>
  );

  return (
    <Panel>
      {/* La tarjeta se aprieta a propósito: en una caseta caben dos o tres
          visitas a la vez, y lo que queda debajo del pliegue no se mira. El
          titular baja de 24 px a 18 —sigue siendo el texto más grande de la
          tarjeta, que es lo que tiene que ser— y los huecos se recortan sin
          llegar a amontonar. Se usa `body-lg` y no un `headline-sm`: ese
          escalón no existe en la escala, y añadirlo al sistema entero por una
          tarjeta sería mover el suelo de todas las pantallas. */}
      <div className="flex items-center justify-between border-b border-outline-variant px-5 py-3">
        <h2 className="text-body-lg font-semibold text-on-surface">
          {fase === 'captura'
            ? 'Colocando identificación'
            : fase === 'credencial'
              ? 'Revisa la identificación'
              : 'Validación de kiosco'}
        </h2>
        <Chip tono={fase === 'autorizacion' ? 'aviso' : 'neutro'}>
          {fase === 'captura' ? 'En vivo' : fase === 'credencial' ? 'Capturada' : 'En proceso'}
        </Chip>
      </div>

      <div className="space-y-3 p-5">
        {/* ── Fase 1: el visitante acomoda y el guardia dirige ── */}
        {fase === 'captura' && (
          <>
            {/* **La charola y la cara, lado a lado.** Apiladas a todo lo ancho
                hacían la tarjeta tan alta que con dos visitas a la vez había
                que desplazarse para ver la segunda — y en una caseta, lo que
                queda debajo del pliegue no se mira. Juntas caben las dos y
                además se comparan de un vistazo, que es justo lo que el guardia
                hace: mirar si quien está en la reja es quien dice el documento.

                Con una sola cámara ocupa el ancho entero, como antes. */}
            <div className={camaraId ? 'grid gap-3 sm:grid-cols-2' : ''}>
              <VideoGuardia accesoId={visita.id} />
              {camaraId && (
                <CamaraDeReja camaraId={camaraId} titulo={visita.carril?.nombre ?? 'la reja'} />
              )}
            </div>
            {destino}
            {visita.instruccion && (
              <p className="rounded-lg bg-secondary-container px-4 py-2 text-body-md text-on-secondary-container">
                {TEXTO_INSTRUCCION[visita.instruccion] ?? visita.instruccion}
              </p>
            )}
            {/* Capturar va primero y destacado: es lo que el guardia pulsa en la
                inmensa mayoría de las visitas. Las correcciones son la excepción
                y no deben competir visualmente con ella. */}
            <div className="flex flex-wrap gap-3 pt-1">
              <Boton
                variante="primario"
                disabled={ocupado}
                onClick={() => onInstruir(visita.id, 'CAPTURAR')}
              >
                Capturar imagen
              </Boton>
              <Boton
                variante="tonal"
                disabled={ocupado}
                onClick={() => onInstruir(visita.id, 'VOLTEAR')}
              >
                Pedir que la voltee
              </Boton>
              <Boton
                variante="tonal"
                disabled={ocupado}
                onClick={() => onInstruir(visita.id, 'NO_ES_CREDENCIAL')}
              >
                No es una identificación
              </Boton>
              {/* La salida cuando algo se atora: el visitante canceló en el
                  kiosco y se fue, o el aparato se quedó a medias. Sin esto la
                  tarjeta se queda en pantalla hasta que vence sola a los diez
                  minutos. Va al final y en `texto` porque es la excepción, no
                  lo que el guardia pulsa a diario. */}
              <Boton
                variante="texto"
                disabled={ocupado}
                onClick={() => onResolver(visita.id, 'rechazar')}
              >
                Cancelar ingreso
              </Boton>
            </div>
          </>
        )}

        {/* ── Fase 2: la foto ya existe; falta darla por buena ── */}
        {fase === 'credencial' && (
          <>
            <FotoSegura foto={identificacion} etiqueta="Identificación" alta />
            {destino}

            {/* Mientras el OCR corre (un par de segundos), todo se bloquea y se
                dice qué está pasando. Sin esto el guardia ve un campo vacío y
                botones activos y cree que el sistema se colgó — o peor, acepta
                con el nombre a medio leer. */}
            {leyendo ? (
              <div className="flex items-center gap-3 rounded-xl bg-surface-container-high px-5 py-4">
                <span className="h-4 w-4 animate-pulse rounded-full bg-primary" />
                <p className="text-body-md text-on-surface">Leyendo credencial…</p>
              </div>
            ) : (
              campoNombre
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Boton
                variante="primario"
                disabled={leyendo || ocupado || nombre.trim().length < 2}
                onClick={() => onAceptarCredencial(visita.id, nombre.trim())}
              >
                {dudosa ? 'Confirmar nombre' : 'Aceptar credencial'}
              </Boton>
              <Boton
                variante="tonal"
                disabled={leyendo || ocupado}
                onClick={() => onInstruir(visita.id, 'REPETIR')}
              >
                Repetir captura
              </Boton>
            </div>
            {!leyendo && nombre.trim().length < 2 && (
              <p className="text-body-md text-on-surface-variant">
                Escribe el nombre de la credencial para poder aceptarla.
              </p>
            )}
          </>
        )}

        {/* ── Fase 3: la decisión de siempre, entrar o no ── */}
        {fase === 'autorizacion' && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <FotoSegura foto={rostro} etiqueta="Rostro" />
              <FotoSegura foto={identificacion} etiqueta="Identificación" />
            </div>
            {destino}
            {campoNombre}
            <dl className="text-body-md text-on-surface-variant">
              <div className="flex justify-between">
                <dt>Llegó</dt>
                <dd>
                  {new Date(visita.entradaEn).toLocaleTimeString('es-MX', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
            </dl>
            {pidiendoMotivo && (
              <div className="rounded-xl border border-error bg-error-container/20 p-4">
                <p className="mb-3 text-body-md text-on-surface">¿Por qué no pasa?</p>
                <div className="flex flex-wrap gap-2">
                  {MOTIVOS_RECHAZO.map((m) => (
                    <Boton
                      key={m}
                      variante="peligro"
                      disabled={ocupado}
                      onClick={() => onResolver(visita.id, 'rechazar', m)}
                    >
                      {m}
                    </Boton>
                  ))}
                  <Boton variante="texto" onClick={() => setPidiendoMotivo(false)}>
                    Mejor no
                  </Boton>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Boton variante="peligro" disabled={ocupado} onClick={() => setPidiendoMotivo(true)}>
                Rechazar
              </Boton>
              <Boton variante="tonal" disabled={ocupado} onClick={() => onOtraFoto(visita.id)}>
                Otra foto
              </Boton>
              <Boton
                variante="primario"
                // Apagado mientras falte el tarjetón, y el botón lo dice en vez
                // de quedarse mudo: un botón gris sin explicación se lee como
                // que el sistema se trabó.
                disabled={ocupado || faltaTarjeton}
                onClick={async () => {
                  // El nombre se guarda antes de abrir: si el guardia lo
                  // corrigió y pulsó Autorizar sin salir del campo, la
                  // corrección se perdería.
                  await guardarNombre();
                  onResolver(visita.id, 'liberar');
                }}
              >
                {ocupado ? 'Abriendo…' : faltaTarjeton ? 'Falta el tarjetón' : 'Autorizar'}
              </Boton>
            </div>
          </>
        )}
      </div>
    </Panel>
  );
}

/**
 * Muestra una foto del acceso.
 *
 * Se descarga con `fetch` y se pinta desde un blob en vez de poner la URL en el
 * `src`: la imagen exige sesión y una etiqueta `<img>` no manda la cabecera de
 * autorización. De paso la URL del objeto muere con el componente, así que la
 * foto no queda colgando en el historial del navegador de la caseta.
 */
function FotoSegura({
  foto,
  etiqueta,
  alta = false,
}: {
  foto?: FotoPendiente;
  etiqueta: string;
  /**
   * Más alta, para cuando la credencial es lo único que se mira.
   *
   * Eran 18rem y ahora son 12: con la de antes, la tarjeta de una sola visita
   * ya no cabía en la pantalla de la caseta, y lo que hay que leer ahí es un
   * nombre —no los detalles del holograma—. Para mirar de cerca está la ficha.
   */
  alta?: boolean;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const objeto = useRef<string | null>(null);

  useEffect(() => {
    let vivo = true;
    if (!foto) return;

    void (async () => {
      try {
        const r = await fetch(`${baseApi()}/api/historial/fotos/${foto.id}`, {
          headers: { Authorization: `Bearer ${almacenToken.leer() ?? ''}` },
        });
        if (!r.ok || !vivo) return;
        const u = URL.createObjectURL(await r.blob());
        objeto.current = u;
        setUrl(u);
      } catch {
        /* una foto que no carga no puede tumbar la pantalla del guardia */
      }
    })();

    return () => {
      vivo = false;
      if (objeto.current) URL.revokeObjectURL(objeto.current);
    };
  }, [foto]);

  return (
    <figure className="overflow-hidden rounded-xl border border-outline-variant bg-surface-container-high">
      {url ? (
        <img
          src={url}
          alt={etiqueta}
          className={`w-full object-contain ${alta ? 'h-48' : 'h-32'}`}
        />
      ) : (
        <div
          className={`flex items-center justify-center text-body-md text-on-surface-variant ${alta ? 'h-72' : 'h-32'}`}
        >
          {foto ? 'Cargando…' : 'Sin foto'}
        </div>
      )}
      <figcaption className="border-t border-outline-variant px-3 py-2 text-label-bold uppercase tracking-wider text-on-surface-variant">
        {etiqueta}
      </figcaption>
    </figure>
  );
}

/** Lo que responde el conmutador sobre una llamada. Nunca trae el número. */
interface Llamada {
  id: string;
  estado:
    | 'SOLICITADA'
    | 'TIMBRANDO'
    | 'CONTESTADA'
    | 'FINALIZADA'
    | 'NO_CONTESTADA'
    | 'OCUPADO'
    | 'FALLIDA';
  etiqueta: string;
  duracionS: number | null;
  motivoFin: string | null;
}

const VIVA = ['SOLICITADA', 'TIMBRANDO', 'CONTESTADA'];

const ESTADO_LLAMADA: Record<string, string> = {
  SOLICITADA: 'Marcando…',
  TIMBRANDO: 'Timbrando…',
  CONTESTADA: 'En línea',
  FINALIZADA: 'Colgó',
  NO_CONTESTADA: 'No contestaron',
  OCUPADO: 'Ocupado',
  FALLIDA: 'No se pudo llamar',
};

/**
 * Llamar a la casa a la que dice que va.
 *
 * Es el paso que cierra el camino de la visita sin invitación: nadie autorizó a
 * esa persona y quien tiene que decir que sí es el residente.
 *
 * **El guardia no ve ningún número, en ningún momento.** Elige de una lista de
 * etiquetas y el teléfono se resuelve en el servidor. Todo el módulo existe por
 * esto: en un condominio normal el guardia marca desde su celular, y el día que
 * renuncia se lleva la libreta del fraccionamiento.
 *
 * Si no contestan, vuelve a pulsar —o prueba el siguiente número de la casa—.
 * La cascada automática no está: insistir por su cuenta hay que ajustarlo
 * viendo casetas de verdad, no adivinándolo.
 */
function LlamarVivienda({ visita }: { visita: VisitaPendiente }) {
  const [llamada, setLlamada] = useState<Llamada | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [marcando, setMarcando] = useState(false);

  const telefonos = visita.unidad?.telefonos ?? [];
  const viva = llamada !== null && VIVA.includes(llamada.estado);

  // Mientras la llamada vive se pregunta cómo va. Al acabar se deja de
  // preguntar: el resultado ya no cambia y el guardia tiene un coche esperando.
  useEffect(() => {
    if (!llamada || !VIVA.includes(llamada.estado)) return;
    const t = setInterval(() => {
      void pedir<Llamada>(`/telefonia/llamadas/${llamada.id}`)
        .then(setLlamada)
        .catch(() => undefined);
    }, 2000);
    return () => clearInterval(t);
  }, [llamada]);

  if (!visita.unidad) return null;

  if (telefonos.length === 0) {
    // Se dice, y se dice qué hacer: un guardia con un visitante enfrente y un
    // botón que no está necesita saber a quién pedírselo.
    return (
      <p className="mt-3 text-label-sm text-on-surface-variant">
        Esta vivienda no tiene teléfono en el padrón. Pídeselo a administración.
      </p>
    );
  }

  async function marcar(orden: number) {
    setError(null);
    setMarcando(true);
    try {
      setLlamada(
        await pedir<Llamada>('/telefonia/llamar', {
          metodo: 'POST',
          cuerpo: { accesoId: visita.id, orden },
        }),
      );
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo llamar');
    } finally {
      setMarcando(false);
    }
  }

  async function colgar() {
    if (!llamada) return;
    try {
      setLlamada(
        await pedir<Llamada>(`/telefonia/llamadas/${llamada.id}/colgar`, { metodo: 'POST' }),
      );
    } catch {
      // Colgar de este lado es cortesía: si falla, el residente cuelga y ya.
    }
  }

  return (
    <div className="mt-4 border-t border-outline-variant pt-3">
      {viva ? (
        <div className="flex flex-col items-center gap-2">
          <p className="text-body-md text-on-surface">
            {ESTADO_LLAMADA[llamada.estado]} · {llamada.etiqueta}
          </p>
          <Boton variante="peligro" onClick={() => void colgar()}>
            Colgar
          </Boton>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-wrap justify-center gap-2">
            {telefonos.map((t) => (
              <Boton
                key={t.orden}
                variante="primario"
                disabled={marcando}
                onClick={() => void marcar(t.orden)}
              >
                {/* Con un solo teléfono la etiqueta sobra y estorba: lo que el
                    guardia necesita leer es la acción. Con varios, la etiqueta
                    ES la elección. */}
                {marcando
                  ? 'Marcando…'
                  : telefonos.length === 1
                    ? 'Llamar a la vivienda'
                    : (t.etiqueta ?? `Teléfono ${t.orden + 1}`)}
              </Boton>
            ))}
          </div>
          {llamada && (
            <p className="text-label-sm text-on-surface-variant">
              {ESTADO_LLAMADA[llamada.estado] ?? llamada.estado}
              {llamada.duracionS ? ` · ${llamada.duracionS} s` : ''}
            </p>
          )}
        </div>
      )}
      {error && <p className="mt-2 text-center text-label-sm text-error">{error}</p>}
    </div>
  );
}

/**
 * El guardia le entrega un tarjetón a una visita sin invitación.
 *
 * Es lo que le da a esa visita el objeto que le faltaba: la que trae QR sale
 * escaneando el mismo con el que entró, y esta no tenía nada que escanear.
 *
 * El campo recibe el escaneo del lector, que se comporta como un teclado: por
 * eso se envía al pulsar Intro y no hay botón obligatorio. Teclearlo también
 * funciona, para el día que el lector no lea.
 */
function EntregarTarjeton({
  visita,
  alEntregar,
}: {
  visita: VisitaPendiente;
  alEntregar: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [entregado, setEntregado] = useState<string | null>(null);

  async function entregar(e: React.FormEvent) {
    e.preventDefault();
    if (!codigo.trim()) return;
    setEnviando(true);
    try {
      const r = await pedir<{ tarjeton: { etiqueta: string | null } }>(
        `/tarjetones/entregar/${visita.id}`,
        { metodo: 'POST', cuerpo: { codigo: codigo.trim() } },
      );
      setEntregado(r.tarjeton.etiqueta ?? 'el tarjetón');
      setCodigo('');
      setAbierto(false);
      // La tarjeta tiene que volver a leerse: de `tarjetonId` depende que
      // el botón de autorizar se encienda.
      alEntregar();
    } catch (err) {
      window.alert(err instanceof ErrorApi ? err.message : 'No se pudo entregar');
    } finally {
      setEnviando(false);
    }
  }

  if (entregado) {
    return (
      <p className="mt-3 text-body-md text-on-surface">
        Tarjetón <b>{entregado}</b> entregado. Pídeselo de vuelta al salir.
      </p>
    );
  }

  if (!abierto) {
    return (
      <div className="mt-3">
        <Boton variante="tonal" onClick={() => setAbierto(true)}>
          Entregar tarjetón
        </Boton>
      </div>
    );
  }

  return (
    <form onSubmit={entregar} className="mt-3 flex flex-wrap items-center justify-center gap-2">
      <Entrada
        autoFocus
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        placeholder="Escanea el tarjetón"
        className="max-w-xs"
      />
      <Boton type="submit" variante="primario" disabled={enviando}>
        {enviando ? 'Entregando…' : 'Entregar'}
      </Boton>
      <Boton type="button" variante="texto" onClick={() => setAbierto(false)}>
        Cancelar
      </Boton>
    </form>
  );
}

/**
 * La visita devuelve su tarjetón y con eso se cierra su ciclo.
 *
 * Vive siempre a la vista y no dentro de una visita concreta, porque el guardia
 * no sabe de quién es el plástico hasta que lo escanea — de eso se encarga el
 * servidor. Buscar antes a la persona en una lista sería pedirle que haga a
 * mano lo que el código ya hace.
 *
 * Se queda puesto lo que pasó, sin temporizador: entre coche y coche el guardia
 * mira otra cosa, y un aviso que se desvanece solo se pierde justo cuando iba a
 * leerlo.
 */
function DevolverTarjeton({ alCerrarVisita }: { alCerrarVisita: () => void }) {
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [salio, setSalio] = useState<{ quien: string; minutos: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function devolver(e: React.FormEvent) {
    e.preventDefault();
    if (!codigo.trim()) return;
    setEnviando(true);
    setError(null);
    try {
      const r = await pedir<{ nombreVisitante: string | null; minutosEstancia: number }>(
        '/tarjetones/devolver',
        { metodo: 'POST', cuerpo: { codigo: codigo.trim() } },
      );
      setSalio({ quien: r.nombreVisitante ?? 'La visita', minutos: r.minutosEstancia });
      setCodigo('');
      alCerrarVisita();
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cerrar');
      setSalio(null);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Panel>
      <div className="flex flex-col gap-3 px-6 py-5">
        <h2 className="text-body-lg font-semibold text-on-surface">Devolver tarjetón</h2>
        <p className="text-body-md text-on-surface-variant">
          Escanéalo cuando la visita te lo entregue. Con eso se cierra su salida.
        </p>
        <form onSubmit={devolver} className="flex flex-wrap gap-2">
          <div className="min-w-0 flex-1">
            <Entrada
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Escanea el tarjetón"
            />
          </div>
          <Boton type="submit" variante="primario" disabled={enviando}>
            {enviando ? 'Cerrando…' : 'Cerrar salida'}
          </Boton>
        </form>

        {salio && (
          <p className="text-body-md text-on-surface">
            <b>{salio.quien}</b> salió. Estuvo {salio.minutos} min.
          </p>
        )}
        {error && <ErrorCaja>{error}</ErrorCaja>}
      </div>
    </Panel>
  );
}
